<template>
    <div>
        <table class="table table-striped table-class customer-highlight-table hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headersToRender"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>
                    <th v-if="!readOnly" scope="col" class="dashboard-sort-header">ACT BTN</th>
                </tr>
            </thead>
            <tbody>
                <template
                    v-for="(item, index) in paginatedData"
                    :key="getItemRowKey(item, index)"
                >
                    <tr :class="rowClass(item)">
                        <td>
                            <button
                                v-if="showServiceCenterDetails(item)"
                                type="button"
                                class="btn btn-link service-center-toggle p-0 me-2"
                                @click="toggleServiceCenters(getItemRowKey(item, index))"
                                :title="
                                    isServiceCentersExpanded(getItemRowKey(item, index))
                                        ? 'Hide service centers'
                                        : 'Show service centers'
                                "
                            >
                                <font-awesome-icon
                                    :icon="[
                                        'fas',
                                        isServiceCentersExpanded(getItemRowKey(item, index))
                                            ? 'chevron-down'
                                            : 'chevron-right',
                                    ]"
                                />
                            </button>
                            <PartNumberDisplay
                                :part-number="item.part_number"
                                :selected-part="part_selected"
                            />
                        </td>
                        <td>{{ getVendorLabel(item) }}</td>
                        <td>
                            <span v-html="getDisplayedDescription(item, index)"></span>&nbsp;
                            <a
                                href="javascript:;"
                                v-if="shouldShowToggle(item.description)"
                                class="toggle-description"
                                @click="toggleRow(index)"
                            >
                                {{ isExpanded(index) ? 'show less' : 'show more' }}
                            </a>
                            <div
                                v-if="showServiceCenterDetails(item)"
                                class="service-centers-summary"
                                role="button"
                                tabindex="0"
                                @click="toggleServiceCenters(getItemRowKey(item, index))"
                                @keydown.enter.prevent="
                                    toggleServiceCenters(getItemRowKey(item, index))
                                "
                                @keydown.space.prevent="
                                    toggleServiceCenters(getItemRowKey(item, index))
                                "
                            >
                                Service centers:
                                {{ getServiceSummary(item).centers_count }} | Total qty:
                                {{ getServiceSummary(item).total_qty }}
                            </div>
                        </td>
                        <td>
                            <template v-if="displayPriceMeta(item).value != null">
                                <div>
                                    {{
                                        formatCurrencyAmount(
                                            displayPriceMeta(item).value,
                                            displayPriceMeta(item).currency
                                        )
                                    }}
                                </div>
                                <div
                                    v-if="!listPriceOnly && shouldShowListPriceBreakdown(item)"
                                    class="price-breakdown"
                                >
                                    List:
                                    {{
                                        formatCurrencyAmount(
                                            getListPriceMeta(item).value,
                                            getListPriceMeta(item).currency
                                        )
                                    }}
                                </div>
                            </template>
                        </td>
                        <td v-if="!listPriceOnly">{{ formatDiscount(item.discount) }}</td>
                        <td>{{ item.uom }}</td>
                        <td>
                            {{
                                getSubstituteForDisplay(
                                    item,
                                    [part_selected],
                                    getSubstituteParentMap()
                                )
                            }}
                        </td>
                        <td>{{ item.manufacturer_name }}</td>
                        <td>{{ item.qty_minimum }}</td>
                        <td>
                            <template v-if="showServiceCenterDetails(item)">
                                <div
                                    class="qty-summary-toggle"
                                    role="button"
                                    tabindex="0"
                                    @click="toggleServiceCenters(getItemRowKey(item, index))"
                                    @keydown.enter.prevent="
                                        toggleServiceCenters(getItemRowKey(item, index))
                                    "
                                    @keydown.space.prevent="
                                        toggleServiceCenters(getItemRowKey(item, index))
                                    "
                                >
                                    <div>{{ getDisplayQty(item) ?? '—' }}</div>
                                    <div class="qty-breakdown">
                                        Service total: {{ getServiceSummary(item).total_qty }}
                                    </div>
                                    <div class="qty-breakdown">
                                        US: {{ getServiceSummary(item).us_qty }}
                                    </div>
                                    <div class="qty-breakdown">
                                        Outside US: {{ getServiceSummary(item).outside_us_qty }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                {{ getSummarizedQty(item) }}
                            </template>
                        </td>
                        <td v-if="!readOnly">
                            <a
                                href="javascript:;"
                                @click="triggerMoreDetails(item)"
                                class="mx-2 dashboard-action-link"
                                title="More details"
                            >
                                <font-awesome-icon :icon="['fas', 'eye']" />
                            </a>
                            <a
                                href="javascript:;"
                                @click="toggleQuoteModal(item)"
                                title="Add Quote"
                                class="dashboard-action-link"
                            >
                                <font-awesome-icon :icon="['fas', 'plus']" />
                            </a>
                        </td>
                    </tr>
                    <tr
                        v-if="
                            showServiceCenterDetails(item) &&
                            isServiceCentersExpanded(getItemRowKey(item, index))
                        "
                        class="service-centers-row"
                    >
                        <td :colspan="headers.length + 1" class="service-centers-container">
                            <div class="service-centers-wrapper">
                                <div class="service-centers-header">
                                    Service Centers ({{ getServiceCenters(item).length }})
                                </div>
                                <table
                                    class="table table-sm table-striped mb-0 service-centers-table"
                                >
                                    <thead>
                                        <tr>
                                            <th>Location</th>
                                            <th>Qty</th>
                                            <th v-if="!listPriceOnly">Your Price</th>
                                            <th>List Price</th>
                                            <th>Currency</th>
                                            <th>
                                                <abbr
                                                    title="Potential Available Date"
                                                    class="service-center-col-abbr"
                                                >
                                                    Pot. Av. Date
                                                </abbr>
                                            </th>
                                            <th>Category</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="center in getServiceCenters(item)"
                                            :key="center.key"
                                        >
                                            <td>{{ center.location }}</td>
                                            <td>
                                                {{
                                                    center.quantity_avail != null
                                                        ? center.quantity_avail
                                                        : '—'
                                                }}
                                            </td>
                                            <td v-if="!listPriceOnly">
                                                {{
                                                    center.price != null
                                                        ? formatCurrencyAmount(
                                                              center.price,
                                                              center.currency
                                                          )
                                                        : '—'
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    center.list_price != null
                                                        ? formatCurrencyAmount(
                                                              center.list_price,
                                                              center.currency
                                                          )
                                                        : '—'
                                                }}
                                            </td>
                                            <td>
                                                <span
                                                    v-if="center.currency"
                                                    :class="getCurrencyClass(center.currency)"
                                                >
                                                    {{ center.currency }}
                                                </span>
                                                <span v-else>—</span>
                                            </td>
                                            <td>{{ center.potential_available_date || '—' }}</td>
                                            <td>{{ center.category || '—' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage <= 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage >= totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatNumeric, normalizePart } from '@/helpers/string.js'
    import {
        buildDashboardRowHighlightClass,
        compareHighlightedCustomerFirst,
        compareSelectedPartFirst,
        getDashboardRowCustomerName,
    } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'
    import {
        firstTier,
        formatCurrencyAmount,
        getDisplayQty,
        getListPriceMeta,
        getQuoteListPrice,
        getQuoteUnitCost,
        getYourPriceMeta,
        getVendorForQuote,
        getServiceCenters as extractServiceCenters,
        getServiceCentersSummary,
        getSubstituteForDisplay,
        getVendorLabel,
        isTextronVendor,
    } from '@/utils/vendorRow'

    export default {
        name: 'PartsVariousSourcesTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
            data: {
                expanded: false,
                type: Array,
                default: () => [],
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            listPriceOnly: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                headers: [
                    { key: 'part_number', label: 'Part Number', type: 'string' },
                    { key: 'vendor', label: 'Vendor', type: 'string' },
                    { key: 'description', label: 'Description', type: 'string' },
                    { key: 'price', label: 'Price', type: 'numeric' },
                    { key: 'discount', label: 'Discount', type: 'numeric' },
                    { key: 'uom', label: 'UOM', type: 'string' },
                    { key: 'substitute_for', label: 'Substitute for', type: 'string' },
                    { key: 'manufacturer_name', label: 'Manufacturer', type: 'string' },
                    { key: 'qty_minimum', label: 'Min Qty', type: 'numeric' },
                    { key: 'display_qty', label: 'Stock', type: 'numeric' },
                ],
                columnType: 'string',
                sortColumn: 0,
                sortDirection: 'asc',
                currentPage: 1,
                itemsPerPage: 10,
                expandedRows: new Set(),
                expandedServiceCenterRows: new Set(),
                maxLength: 90,
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal', 'master_substitutes']),
            headersToRender() {
                return this.headers
                    .filter((header) => !(this.listPriceOnly && header.key === 'discount'))
                    .map((header) =>
                        this.listPriceOnly && header.key === 'price'
                            ? { ...header, label: 'List Price' }
                            : header
                    )
            },
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const partPriority = compareSelectedPartFirst(
                        a?.part_number,
                        b?.part_number,
                        this.part_selected
                    )
                    if (partPriority !== 0) return partPriority

                    const customerPriority = compareHighlightedCustomerFirst(
                        getDashboardRowCustomerName(a),
                        getDashboardRowCustomerName(b),
                        this.highlightedCustomer
                    )
                    if (customerPriority !== 0) return customerPriority

                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = this.getSortValue(a, key)
                    let valueB = this.getSortValue(b, key)

                    if (this.columnType === 'numeric') {
                        valueA = Number(String(valueA).replace('%', '')) || 0
                        valueB = Number(String(valueB).replace('%', '')) || 0
                    } else if (this.columnType === 'date') {
                        valueA = new Date(valueA)
                        valueB = new Date(valueB)
                    } else {
                        valueA = String(valueA).toLowerCase()
                        valueB = String(valueB).toLowerCase()
                    }

                    if (valueA === valueB) {
                        return this.getItemRowKey(a, 0).localeCompare(this.getItemRowKey(b, 0))
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.data.length / this.itemsPerPage))
            },
        },
        watch: {
            part_selected() {
                this.currentPage = 1
            },
        },
        methods: {
            formatNumeric,
            formatDiscount(value) {
                return value === null || value === undefined || value === ''
                    ? ''
                    : formatNumeric(value, 'percentWhole')
            },
            formatCurrencyAmount,
            firstTier,
            getVendorLabel,
            getSubstituteForDisplay,
            getDisplayQty,
            getListPriceMeta,
            getYourPriceMeta,
            getServiceCenters(item) {
                return extractServiceCenters(item)
            },
            getServiceSummary(item) {
                return getServiceCentersSummary(item)
            },
            displayPriceMeta(item) {
                return this.listPriceOnly ? this.getListPriceMeta(item) : this.getYourPriceMeta(item)
            },
            isTextronRow(item) {
                return isTextronVendor(item)
            },
            hasServiceCenters(item) {
                return this.isTextronRow(item) && this.getServiceCenters(item).length > 0
            },
            showServiceCenterDetails(item) {
                return !this.readOnly && this.hasServiceCenters(item)
            },
            getItemRowKey(item, index) {
                return [
                    item?.source,
                    item?.ui_vendor_name || item?.vendor,
                    item?.part_number,
                    item?.part_number_manufacturer,
                    item?.vendor_part_number,
                    item?.substitute_for,
                    item?.manufacturer_name,
                    item?.unique_id_hash || item?.id || index,
                ]
                    .filter((value) => value !== undefined && value !== null && value !== '')
                    .join('|')
            },
            isServiceCentersExpanded(rowKey) {
                return this.expandedServiceCenterRows.has(rowKey)
            },
            toggleServiceCenters(rowKey) {
                if (this.readOnly) {
                    return
                }
                if (this.isServiceCentersExpanded(rowKey)) {
                    this.expandedServiceCenterRows.delete(rowKey)
                } else {
                    this.expandedServiceCenterRows.add(rowKey)
                }
            },
            getSummarizedQty(item) {
                return this.getDisplayQty(item)
            },
            getSubstituteParentMap() {
                return this.master_substitutes?.substitute_parent_map || {}
            },
            stripHtmlAndKeepSpaces(content) {
                if (!content) {
                    return ''
                }

                const withLineBreaks = content.replace(/<br\s*\/?>/gi, '\n')
                const textOnly = withLineBreaks.replace(/<[^>]+>/g, ' ')
                const cleannedString = textOnly
                    .replace(/[ \t]+/g, ' ')
                    .replace(/\n\s*/g, '\n')
                    .trim()

                return cleannedString.slice(0, this.maxLength) + '...'
            },
            hasHTML(content) {
                const htmlTagRegex = /<[^>]+>/
                return htmlTagRegex.test(content)
            },
            isExpanded(index) {
                return this.expandedRows.has(index)
            },
            toggleRow(index) {
                if (this.isExpanded(index)) {
                    this.expandedRows.delete(index)
                } else {
                    this.expandedRows.add(index)
                }
            },
            shouldShowToggle(content) {
                return content && content.length > this.maxLength
            },
            getDisplayedDescription(row, index) {
                const content = row.description || ''
                if (this.isExpanded(index) || content.length <= this.maxLength) {
                    return content
                }
                if (this.hasHTML(content)) {
                    return this.stripHtmlAndKeepSpaces(content)
                }
                return content.slice(0, this.maxLength) + '...'
            },
            triggerMoreDetails(details) {
                this.$emit('more-details', details)
            },
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                return this.headersToRender[index]?.key
            },
            getSortValue(row, key) {
                if (key === 'price') {
                    return this.displayPriceMeta(row).value ?? 0
                }
                if (key === 'display_qty') {
                    return this.getSummarizedQty(row)
                }
                if (key === 'vendor') {
                    return this.getVendorLabel(row)
                }
                if (key === 'substitute_for') {
                    return this.getSubstituteForDisplay(
                        row,
                        [this.part_selected],
                        this.getSubstituteParentMap()
                    )
                }
                return row?.[key]
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            shouldShowListPriceBreakdown(item) {
                if (!this.isTextronRow(item)) {
                    return false
                }
                const yourPrice = this.getYourPriceMeta(item).value
                const listPrice = this.getListPriceMeta(item).value
                return listPrice != null && listPrice !== yourPrice
            },
            toggleQuoteModal(item) {
                const mappedVendorForQuote = getVendorForQuote(item)
                this.$emit('toggleQuoteModal', {
                    ...item,
                    part_quoted: item?.part_number || '',
                    vendor: mappedVendorForQuote,
                    ui_vendor_name: mappedVendorForQuote,
                    manufacturer_name: item?.manufacturer_name || '',
                    unit_cost: getQuoteUnitCost(item),
                    list_price: getQuoteListPrice(item),
                    description: item?.description || '',
                })
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(item) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    customerName: getDashboardRowCustomerName(item),
                    selectedPart: this.part_selected,
                    partNumber: item?.part_number,
                    activePart: this.isActivePart(item.part_number),
                })
            },
            getCurrencyClass(currency) {
                const normalized = String(currency || '')
                    .trim()
                    .toUpperCase()
                if (normalized === 'USD') {
                    return 'currency-pill currency-usd'
                }
                if (normalized === 'EUR') {
                    return 'currency-pill currency-eur'
                }
                return 'currency-pill currency-default'
            },
        },
    }
</script>

<style scoped>
    .toggle-description {
        color: var(--color-brand-primary);
        cursor: pointer;
        font-weight: 600;
        text-decoration: none;
    }

    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .service-center-toggle {
        color: var(--color-text-primary);
        text-decoration: none;
        vertical-align: middle;
    }

    .service-centers-summary {
        margin-top: 4px;
        color: var(--color-text-muted);
        font-size: 12px;
        cursor: pointer;
    }

    .qty-breakdown {
        font-size: 12px;
        color: var(--color-text-muted);
        line-height: 1.2;
    }

    .price-breakdown {
        font-size: 12px;
        color: var(--color-text-muted);
        line-height: 1.2;
    }

    .qty-summary-toggle {
        cursor: pointer;
    }

    .service-centers-row td {
        background: var(--color-surface-subtle);
    }

    .service-centers-container {
        padding: 0.75rem 1rem !important;
    }

    .service-centers-wrapper {
        border: 1px solid var(--color-border-muted);
        border-radius: var(--radius-sm);
        background: var(--color-surface-canvas);
        overflow: hidden;
    }

    .service-centers-header {
        padding: 0.5rem 0.75rem;
        font-weight: 600;
        background: var(--color-surface-subtle);
        border-bottom: 1px solid var(--color-border-muted);
    }

    .service-centers-table th,
    .service-centers-table td {
        font-size: 12px;
        vertical-align: middle;
    }

    .currency-pill {
        display: inline-block;
        padding: 0.1rem 0.45rem;
        border-radius: var(--radius-pill);
        font-weight: 700;
        letter-spacing: 0.2px;
        border: 1px solid transparent;
    }

    .currency-usd {
        background: var(--color-state-success);
        color: var(--color-text-inverse);
        border-color: var(--color-state-success-strong);
    }

    .currency-eur {
        background: var(--color-state-info);
        color: var(--color-text-inverse);
        border-color: var(--color-state-info-strong);
    }

    .currency-default {
        background: var(--color-text-secondary);
        color: var(--color-text-inverse);
        border-color: var(--color-text-primary);
    }

    .service-center-col-abbr {
        text-decoration: none;
        border-bottom: 1px dotted var(--color-text-muted);
        cursor: help;
    }
</style>
